import { useAPI } from "contexts/APIContext";
import React, { useCallback, useEffect, useState } from "react";
import { emailOrPhoneSchema } from "schemas/auth";
import { APIError } from "snudbud-backbone-sdk";
import handleError from "utils/handleError";

// Using the API's access verification types
type AccessVerificationType = "sign-in" | "sign-up" | "update-email" | "update-phone" | "delete-user";

interface AccessVerificationProps {
  channel?: "email" | "phone";
  className?: string;
  emailOrPhone?: string;
  onCancel?: () => void;
  onStep?: (data: { emailOrPhone: string | undefined }) => void;
  onComplete: (data: { emailOrPhone: string; token: string; type: AccessVerificationType }) => void;
  type?: AccessVerificationType;
}

/**
 * Component that handles the two-step verification flow (email/phone + code)
 */
const AccessVerification: React.FC<AccessVerificationProps> = function AccessVerificationComponent({
  channel = "email",
  className,
  emailOrPhone: initialEmailOrPhone,
  onCancel,
  onStep,
  onComplete,
  type: initialType = "sign-in",
}) {
  const { api } = useAPI();

  const [loading, setLoading] = useState(false);
  const [accessVerificationId, setAccessVerificationId] = useState<string>();
  const [code, setCode] = useState<string>("");
  const [emailOrPhone, setEmailOrPhone] = useState<string>(initialEmailOrPhone || "");
  const [error, setError] = useState<string>();
  const [fieldError, setFieldError] = useState<{ [key: string]: string }>({});
  const [type, setType] = useState<AccessVerificationType>(initialType);

  const handleChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(undefined);
    setFieldError({});
    setCode(e.target.value);
  };

  const handleChangeEmailOrPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(undefined);
    setFieldError({});
    setEmailOrPhone(e.target.value);
  };

  const handleClickBack = () => {
    if (!accessVerificationId) return onCancel?.();

    setAccessVerificationId(undefined);
    setCode("");
    onStep?.({ emailOrPhone: undefined });
  };

  const handleRequestCode = useCallback(
    async (args: { type?: AccessVerificationType } = {}): Promise<any> => {
      if (!emailOrPhone) return;

      // Validate email/phone format
      const validationResult = emailOrPhoneSchema.safeParse(emailOrPhone);
      if (!validationResult.success) {
        setFieldError({ emailOrPhone: validationResult.error.errors[0]?.message || "Invalid input" });
        return;
      }

      setError(undefined);
      setFieldError({});

      try {
        setLoading(true);

        // Map the web app type to the API type
        const apiType = args.type || type;

        const { accessVerificationId } = await api.accessVerification.begin({
          emailOrPhone,
          type: apiType,
        });

        setAccessVerificationId(accessVerificationId);
        setType(args.type || type);
        onStep?.({ emailOrPhone });
      } catch (e: any) {
        if (!e.code) return setError(handleError(e));

        // If user not found when signing in, try signing up instead
        if (e.code === APIError.NotFoundCode.UserNotFound) return await handleRequestCode({ type: "sign-up" });

        // Handle specific field errors
        if (e.fields?.emailOrPhone) {
          setFieldError({
            emailOrPhone:
              e.response.data.code === APIError.NotFoundCode.UserNotFound
                ? "User does not exist."
                : e.response.status === 400
                ? `The ${channel === "email" ? "email" : "phone number"} is invalid.`
                : e.response.data.fields.emailOrPhone,
          });
        } else {
          setError(handleError(e));
        }
      } finally {
        setLoading(false);
      }
    },
    [api, emailOrPhone, onStep, type, channel]
  );

  const handleConfirmCode = useCallback(async () => {
    if (!accessVerificationId || !code || !emailOrPhone) return;

    setError(undefined);
    setFieldError({});

    let token;
    try {
      setLoading(true);
      token = (await api.accessVerification.complete({ accessVerificationId, code })).token;
    } catch (e: any) {
      if (e.response?.data?.fields?.code) {
        setFieldError({
          code: e.response.status === 400 ? "The verification code is invalid." : e.response.data.fields.code,
        });
      } else {
        setError(handleError(e));
      }
      setLoading(false);
      return;
    }

    setLoading(false);
    onComplete({ emailOrPhone, token, type });
  }, [accessVerificationId, api, code, emailOrPhone, type, onComplete]);

  // Update state when props change
  useEffect(() => {
    if (initialEmailOrPhone) {
      setEmailOrPhone(initialEmailOrPhone);
    }
  }, [initialEmailOrPhone]);

  useEffect(() => {
    if (initialType) {
      setType(initialType);
    }
  }, [initialType]);

  return (
    <div className={`flex flex-col space-y-6 ${className || ""}`}>
      <div className="space-y-4">
        <div>
          <label htmlFor="emailOrPhone" className="block text-sm font-medium text-gray-700">
            {channel === "email" ? "Email address" : "Phone number"}
          </label>
          <div className="mt-1">
            <input
              type={channel === "email" ? "email" : "tel"}
              id="emailOrPhone"
              name="emailOrPhone"
              autoComplete={channel === "email" ? "email" : "tel"}
              disabled={Boolean(accessVerificationId || initialEmailOrPhone)}
              value={emailOrPhone}
              onChange={handleChangeEmailOrPhone}
              className={`appearance-none block w-full px-3 py-2 border ${
                fieldError.emailOrPhone ? "border-red-300" : "border-gray-300"
              } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder={channel === "email" ? "john.doe@example.com" : "+1 (123) 456-7890"}
            />
            {fieldError.emailOrPhone && <p className="mt-2 text-sm text-red-600">{fieldError.emailOrPhone}</p>}
            <p className="mt-2 text-sm text-gray-500">
              A confirmation code {accessVerificationId ? "has been" : "will be"} sent.
            </p>
          </div>
        </div>

        {accessVerificationId && (
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700">
              Verification Code
            </label>
            <div className="mt-1">
              <input
                type="text"
                inputMode="numeric"
                id="code"
                name="code"
                value={code}
                onChange={handleChangeCode}
                className={`appearance-none block w-full px-3 py-2 border ${
                  fieldError.code ? "border-red-300" : "border-gray-300"
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="123456"
                autoFocus
              />
              {fieldError.code && <p className="mt-2 text-sm text-red-600">{fieldError.code}</p>}
            </div>
          </div>
        )}

        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>

      <div className="flex gap-4">
        {accessVerificationId ? (
          <>
            <button
              type="button"
              onClick={handleClickBack}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleConfirmCode}
              disabled={loading || !code}
              className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Confirm"}
            </button>
          </>
        ) : (
          <>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => handleRequestCode()}
              disabled={loading || !emailOrPhone}
              className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send Code"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AccessVerification;
